import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import MarketplaceShell from './MarketplaceShell';
import { MktCard, MktButton } from './marketplaceUi';
import { getArchetypeProfile, type ArchetypeProfile as Profile } from './marketplaceApi';

const FIELD_LABELS: Record<string, string> = {
  age: 'Age',
  gender: 'Gender',
  income: 'Household income',
  children: 'Children',
  location: 'Location',
  ethnicity: 'Ethnicity',
  education: 'Education',
  employment: 'Employment',
  occupation: 'Occupation',
  income_level: 'Income',
};

const ArchetypeProfileScreen: React.FC = () => {
  const { archetypeId = '' } = useParams();
  const navigate = useNavigate();
  const [profile, setProfile] = useState<Profile | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    getArchetypeProfile(archetypeId).then(setProfile).catch((e) => setError(e.message));
  }, [archetypeId]);

  if (error) {
    return <MarketplaceShell maxWidth="max-w-3xl"><div className="text-red-600">{error}</div></MarketplaceShell>;
  }
  if (!profile) {
    return <MarketplaceShell maxWidth="max-w-3xl"><div className="text-[13px] text-[#6b6b6b]">Loading…</div></MarketplaceShell>;
  }

  const fields = Object.entries(profile.sample_profile || {});

  return (
    <MarketplaceShell maxWidth="max-w-3xl">
      <button
        onClick={() => navigate(-1)}
        className="mb-6 inline-flex items-center gap-1 text-[13px] text-[#6b6b6b] hover:text-[#111]"
      >
        <ArrowLeft className="h-4 w-4" /> Back
      </button>

      <div>
        <div className="text-[11px] font-medium uppercase tracking-[0.05em] text-[#999]">{profile.sub_category_name}</div>
        <h1 className="text-[22px] font-medium text-[#111]">{profile.name}</h1>
      </div>
      <p className="mt-2 text-[13px] text-[#6b6b6b]">
        {profile.twin_count} AI Twins built from real human surveys.
      </p>

      <MktCard className="mt-6 p-6">
        <h2 className="mb-4 text-[13px] font-medium text-[#111]">Representative Twin</h2>
        {fields.length === 0 ? (
          <p className="text-[13px] text-[#999]">Profile details available after you run a study.</p>
        ) : (
          <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {fields.map(([k, v]) => (
              <div key={k}>
                <dt className="text-[11px] text-[#999]">{FIELD_LABELS[k] || k}</dt>
                <dd className="text-[13px] text-[#374151]">{String(v)}</dd>
              </div>
            ))}
          </dl>
        )}
      </MktCard>

      <div className="mt-8">
        <MktButton
          onClick={() => navigate('/marketplace/run', { state: { archetypeId: profile.archetype_id, subCategory: profile.sub_category } })}
        >
          Run a study with this segment →
        </MktButton>
      </div>
    </MarketplaceShell>
  );
};

export default ArchetypeProfileScreen;
